import { useEffect, useState } from "react";
import {
  cachedDatamuse,
  type DatamuseFailure,
  failureOf,
  fetchDatamuse,
  type WordResult,
} from "@/lib/tool-kit/vocabulary/datamuse";

export type LookupStatus = "idle" | "loading" | "ready" | "error";

export interface DatamuseLookup {
  status: LookupStatus;
  /** One answer per query, in the order the queries were given. */
  results: WordResult[][];
  failure: DatamuseFailure | null;
  /** The queries `results` answers — compare with the current ones to spot stale data. */
  answered: string;
  retry: () => void;
}

interface LookupState {
  answered: string;
  status: LookupStatus;
  results: WordResult[][];
  failure: DatamuseFailure | null;
}

const IDLE: LookupState = {
  answered: "",
  status: "idle",
  results: [],
  failure: null,
};

/** Separates the attempt counter from the queries; `|` is always percent-encoded in them. */
const ATTEMPT_MARK = "|";
const QUERY_SEPARATOR = "\n";

/**
 * Run one or more Datamuse queries for the current input, debounced.
 *
 * Typing "happ" then "happy" sends one request, not two: the lookup waits `delayMs`
 * after the last change, and a change inside the window cancels both the timer and any
 * request already in flight (AbortController), so a slow answer to an old word can
 * never replace the answer to the new one. Answers already in memory are shown at once,
 * without the wait. Pass no queries to clear.
 */
export function useDatamuse(
  queries: readonly string[],
  delayMs = 350
): DatamuseLookup {
  const [state, setState] = useState<LookupState>(IDLE);
  const [attempt, setAttempt] = useState(0);
  const joined = queries.join(QUERY_SEPARATOR);
  const request = joined ? `${attempt}${ATTEMPT_MARK}${joined}` : "";

  useEffect(() => {
    if (!request) {
      setState(IDLE);
      return;
    }
    const key = request.slice(request.indexOf(ATTEMPT_MARK) + 1);
    const list = key.split(QUERY_SEPARATOR);
    const cached = list.map(cachedDatamuse);
    if (cached.every((hit) => hit !== undefined)) {
      setState({
        answered: key,
        status: "ready",
        results: cached as WordResult[][],
        failure: null,
      });
      return;
    }
    const controller = new AbortController();
    setState((previous) => ({ ...previous, status: "loading", failure: null }));
    const timer = setTimeout(() => {
      Promise.all(list.map((query) => fetchDatamuse(query, controller.signal)))
        .then((results) => {
          if (!controller.signal.aborted) {
            setState({
              answered: key,
              status: "ready",
              results,
              failure: null,
            });
          }
        })
        .catch((error: unknown) => {
          if (!controller.signal.aborted) {
            setState({
              answered: key,
              status: "error",
              results: [],
              failure: failureOf(error),
            });
          }
        });
    }, delayMs);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [request, delayMs]);

  return {
    ...state,
    retry: () => setAttempt((count) => count + 1),
  };
}
